import { HealthBar } from './HealthBar';
import { HealthDelta } from './HealthDelta';
import { DataFreshnessBadge, type FreshnessTone } from './DataFreshnessBadge';
import { TopologyStrip, type TopologyNode } from './TopologyStrip';
import { StatusPill, type StatusLevel } from './StatusPill';

export type AssetHeaderAsset = {
  id: string;
  name: string;
  klass: string; // "Pump", "Motor", etc.
  site: string;
  criticality: 'low' | 'medium' | 'high';
  health: number;
  previousHealth?: number;
  thresholds: { warn: number; critical: number };
};

export type AssetHeaderProps = {
  asset: AssetHeaderAsset;
  topology?: { nodes: TopologyNode[]; impactedIds?: string[] };
  /**
   * Freshness badge in the top-right of the header. Optional — when the
   * surrounding screen already shows a page-level freshness indicator
   * (e.g. S1's "Requires attention · updated just now"), omit this prop
   * to avoid duplicating the same chip 80px away on the same row.
   */
  freshness?: { at: Date; tone: FreshnessTone };
  variant?: 'hero' | 'band';
  alert?: { level: StatusLevel; label: string };
};

const CRIT_LABEL: Record<AssetHeaderAsset['criticality'], string> = {
  low: 'Low criticality',
  medium: 'Medium criticality',
  high: 'High criticality',
};

/**
 * Asset identity bar. Hero variant is the page header for S3 Asset
 * Detail; band variant is the compact strip used inside list pages.
 */
export function AssetHeader({
  asset,
  topology,
  freshness,
  variant = 'hero',
  alert,
}: AssetHeaderProps) {
  const isHero = variant === 'hero';
  return (
    <header
      className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      data-variant={variant}
    >
      <div className="flex items-start justify-between gap-sp-4">
        <div className="min-w-0">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            {asset.klass} · {asset.site} · {CRIT_LABEL[asset.criticality]}
          </p>
          <h1
            className={
              isHero
                ? 'mt-sp-2 text-fs-h1 font-medium text-gray-900'
                : 'mt-sp-2 text-fs-statement font-medium text-gray-900'
            }
          >
            {asset.name}
          </h1>
          <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
            {asset.id}
          </p>
        </div>
        <div className="flex items-center gap-sp-3">
          {alert ? <StatusPill level={alert.level} label={alert.label} /> : null}
          {freshness ? (
            <DataFreshnessBadge at={freshness.at} tone={freshness.tone} />
          ) : null}
        </div>
      </div>

      <div className={isHero ? 'mt-sp-6' : 'mt-sp-3'}>
        {/* Labelled health rail. The "Health" caption + threshold ticks
            anchor the rendered score so operators see "Health · NN
            (Critical)" as a single unit, not a colored gradient with a
            number floating at the far right. Previous-health delta
            ("was 65 24h ago") only renders when we have a value to
            compare against; without it the bar's ghost-tick is
            ambiguous. */}
        <div className="flex flex-wrap items-baseline justify-between gap-sp-2">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Health
          </p>
          {isHero && asset.previousHealth !== undefined ? (
            <HealthDelta
              previous={asset.previousHealth}
              current={asset.health}
            />
          ) : null}
        </div>
        <div className="mt-sp-2">
          <HealthBar
            size={isHero ? 'hero' : 'row'}
            value={asset.health}
            previous={asset.previousHealth}
            thresholds={asset.thresholds}
          />
        </div>
        {isHero ? (
          <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
            0 ·{' '}
            <span className="text-critical">crit {asset.thresholds.critical}</span>
            {' · '}
            <span className="text-warn-fg">warn {asset.thresholds.warn}</span>
            {' · 100'}
          </p>
        ) : null}
      </div>

      {topology ? (
        <details className="mt-sp-4 [&_summary]:cursor-pointer">
          <summary className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700 marker:text-gray-700">
            Topology
          </summary>
          <div className="mt-sp-2 overflow-x-auto opacity-80">
            <TopologyStrip
              nodes={topology.nodes}
              focusId={asset.id}
              impactedIds={topology.impactedIds}
            />
          </div>
        </details>
      ) : null}
    </header>
  );
}
